import { Divider, Paper, Stack, Typography } from '@mui/material';
import type { ChartsAxisContentProps } from '@mui/x-charts/ChartsTooltip';
import { dayjs, isEmptyArray, isNullOrUndefined } from '@weft/utils';
import { formatNumber } from '@weft/utils';
import { ColorDot } from 'apps/app/src/components/design/ColorDot';
import { last } from 'lodash';
import { DISPLAY_INTERVALS, type EventsDigestedLineChartDisplayInterval } from './events-digested-line-chart';
import { getCurrentMonthChunkDates } from './events-digested-line-chart-utils';

type EventsDigestedLineChartToolTipProps = {
  displayInterval: EventsDigestedLineChartDisplayInterval;
  eventsPerMonth: number;
} & ChartsAxisContentProps;

export const EventsDigestedLineChartToolTip: React.FC<EventsDigestedLineChartToolTipProps> = ({ axisValue, dataIndex, series, displayInterval, eventsPerMonth }) => {
  if (isNullOrUndefined(dataIndex)) {
    return null;
  }

  const label = getFormattedTooltipLabel(dayjs(axisValue).toDate(), displayInterval, eventsPerMonth);
  const [firstSeries] = series;
  const seriesItemData = firstSeries.data[dataIndex];
  const itemValue = typeof seriesItemData === 'number' ? formatNumber(seriesItemData) : '--';

  return (
    <Paper sx={{
      backgroundColor: '#0D1B27DE',
      width: '200px',
      transform: 'translate(-50%, -120%)',
    }}
    >
      <Stack
        direction="column"
        alignItems="start"
        width="100%"
        gap={1}
      >
        <Typography
          fontSize="15px"
          lineHeight="18px"
          fontWeight={500}
          textAlign="center"
          color="white"
          paddingTop={1}
          paddingX={1}
        >
          Events Digested
        </Typography>
        <Divider
          color='#37474F'
          sx={{ width: '100%' }}
        />
        <Stack
          direction="row"
          width="100%"
        >
          <Stack
            paddingBottom={1}
            paddingX={1}
            direction="row"
            alignItems="center"
            gap={0.5}
          >
            <ColorDot
              sizeOverride='8px'
              color="#C250FF"
            />
            <Typography
              fontSize="14px"
              lineHeight="18px"
              fontWeight={400}
              textAlign="center"
              color="white"
            >
              {itemValue}
            </Typography>
          </Stack>
          <Typography
            fontSize="14px"
            lineHeight="18px"
            fontWeight={400}
            textAlign="right"
            marginLeft="auto"
            paddingRight={1}
            color="#90A4AE"
          >
            {label}
          </Typography>
        </Stack>
      </Stack>            
    </Paper>
  );
};

export function getFormattedTooltipLabel(date: Date, displayInterval: EventsDigestedLineChartDisplayInterval, eventsPerMonth: number) {
  if (displayInterval === DISPLAY_INTERVALS.daily) {
    return dayjs(date).format('MMM DD');
  }

  const currentMonthChunkDates = getCurrentMonthChunkDates(date, eventsPerMonth);

  const [firstDate] = currentMonthChunkDates;
  const lastDate = last(currentMonthChunkDates);

  return isEmptyArray(currentMonthChunkDates) || firstDate.isSame(lastDate) 
    ? `${dayjs(date).format('MMM DD')}` 
    : `${dayjs(firstDate).format('MMM DD')} - ${dayjs(lastDate).format('MMM DD')}`;
}